import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rpcClient } from 'src/api/rpc/client';
import type {
  StatefulTableDefinitionResponse,
  StatefulTableVersionResponse,
  StatefulTableFrameResponse,
  StatefulTableLookupResponse,
  StatefulTableFrameHistoryEntry,
} from '@erp/rpc';

const STATEFUL_TABLES_KEY = 'stateful-tables';

export function useListStatefulTables(
  params: {
    page?: number;
    perPage?: number;
  } = {},
) {
  return useQuery<{
    results: StatefulTableDefinitionResponse[];
    page: number;
    perPage: number;
    total: number;
  }>({
    queryKey: [STATEFUL_TABLES_KEY, 'list', params],
    queryFn: () => rpcClient.statefulTables.list(params),
  });
}

export function useGetStatefulTable(id: string | undefined) {
  return useQuery<{ definition: StatefulTableDefinitionResponse }>({
    queryKey: [STATEFUL_TABLES_KEY, 'detail', id],
    queryFn: () => {
      if (!id) throw new Error('useGetStatefulTable: id is required');
      return rpcClient.statefulTables.get({ id });
    },
    enabled: !!id,
  });
}

export function useGetLatestFrame(id: string | undefined) {
  return useQuery<{ frame: StatefulTableFrameResponse }>({
    queryKey: [STATEFUL_TABLES_KEY, 'latest-frame', id],
    queryFn: () => {
      if (!id) throw new Error('useGetLatestFrame: id is required');
      return rpcClient.statefulTables.getLatestFrame({ id });
    },
    enabled: !!id,
    retry: false,
  });
}

export function useListFrames(
  id: string | undefined,
  params: { page?: number; perPage?: number } = {},
) {
  return useQuery<{
    results: StatefulTableFrameHistoryEntry[];
    page: number;
    perPage: number;
    total: number;
  }>({
    queryKey: [STATEFUL_TABLES_KEY, 'frames', id, params],
    queryFn: () => {
      if (!id) throw new Error('useListFrames: id is required');
      return rpcClient.statefulTables.listFrames({ id, ...params });
    },
    enabled: !!id,
  });
}

export function useStatefulTableLookup(
  id: string | undefined,
  key: string | undefined,
) {
  return useQuery<{ lookup: StatefulTableLookupResponse }>({
    queryKey: [STATEFUL_TABLES_KEY, 'lookup', id, key],
    queryFn: () => {
      if (!id || !key)
        throw new Error('useStatefulTableLookup: id and key are required');
      return rpcClient.statefulTables.lookupByKey({ id, key });
    },
    enabled: !!id && !!key,
  });
}

export interface CreateStatefulTableInput {
  name: string;
  description?: string;
  columns: Array<{
    name: string;
    type: 'string' | 'integer' | 'float' | 'numeric' | 'boolean' | 'date';
    description: string;
  }>;
  keyColumns: string[];
}

export interface ConfirmMatchesInput {
  id: string;
  frameNumber: number;
  matches: Array<{ schema_column: string; input_column: string | null }>;
}

export function useCreateStatefulTable() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateStatefulTableInput) =>
      rpcClient.statefulTables.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [STATEFUL_TABLES_KEY] });
    },
  });
}

export function useDeleteStatefulTable() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string }) =>
      rpcClient.statefulTables.delete(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [STATEFUL_TABLES_KEY] });
    },
  });
}

export function useCreateStatefulTableVersion() {
  const queryClient = useQueryClient();
  return useMutation<
    {
      version: StatefulTableVersionResponse;
    },
    Error,
    {
      id: string;
      columns: CreateStatefulTableInput['columns'];
      keyColumns: string[];
    }
  >({
    mutationFn: (input) => rpcClient.statefulTables.createVersion(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [STATEFUL_TABLES_KEY] });
    },
  });
}

export function useConfirmStatefulTableMatches() {
  const queryClient = useQueryClient();
  return useMutation<
    { frame: StatefulTableFrameResponse },
    Error,
    ConfirmMatchesInput
  >({
    mutationFn: (input) => rpcClient.statefulTables.confirmMatches(input),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({
        queryKey: [STATEFUL_TABLES_KEY, 'frames', id],
      });
      queryClient.invalidateQueries({
        queryKey: [STATEFUL_TABLES_KEY, 'latest-frame', id],
      });
    },
  });
}
